import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";
import logo from "@/assets/logo.png";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { useApp } from "@/context/AppContext";
import { useFinance } from "@/context/FinanceContext";
import { REGISTER } from "@/constants/testIds";

export default function Register() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/";
  const { register } = useAuth();
  const { report } = useApp();
  const { setupCompleted } = useFinance();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password minimal 6 karakter");
    if (password !== confirm) return toast.error("Konfirmasi password tidak cocok");
    setBusy(true);
    try {
      const d = await register(email.trim(), password);
      if (d.claimedReports > 0) toast.success("Akun dibuat & laporan tersimpan di akun Anda");
      else toast.success("Akun berhasil dibuat");
      const explicitNext = params.get("next");
      navigate(explicitNext || "/app/beranda");
    } catch (err) {
      toast.error(err.message || "Gagal mendaftar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto min-h-screen w-full max-w-md lg:max-w-lg bg-white px-6 pt-6" data-testid="register-page">
      <header className="mb-8 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-zinc-500" data-testid="register-back">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <img src={logo} alt="CeKKeU" data-testid="auth-logo" className="h-14 w-auto" />
      </header>

      <h1 className="font-heading text-3xl font-extrabold tracking-tight text-zinc-900">Buat akun</h1>
      <p className="mt-2 text-sm text-zinc-500">
        Simpan laporan Anda dengan aman. Kami hanya menyimpan ringkasan ketahanan — angka keuangan yang Anda masukkan tidak dikirim ke server kami.
      </p>

      <form onSubmit={submit} className="mt-8 space-y-4">
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-zinc-700 font-heading">Email</Label>
          <Input
            data-testid={REGISTER.emailInput}
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="nama@email.com"
            className="h-12 rounded-2xl border-zinc-200 text-base"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-zinc-700 font-heading">Password</Label>
          <Input
            data-testid={REGISTER.passwordInput}
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Minimal 6 karakter"
            className="h-12 rounded-2xl border-zinc-200 text-base"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-zinc-700 font-heading">Konfirmasi password</Label>
          <Input
            data-testid={REGISTER.passwordConfirmInput}
            type="password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Ulangi password"
            className="h-12 rounded-2xl border-zinc-200 text-base"
          />
        </div>

        <Button
          data-testid={REGISTER.submitButton}
          type="submit"
          disabled={busy}
          className="h-14 w-full rounded-full bg-zinc-900 text-base font-semibold hover:bg-zinc-800"
        >
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Daftar"}
        </Button>
      </form>

      <p className="mt-6 text-center text-sm text-zinc-500">
        Sudah punya akun?{" "}
        <Link
          to={`/login?next=${encodeURIComponent(next)}`}
          data-testid={REGISTER.loginLink}
          className="font-semibold text-emerald-600"
        >
          Masuk
        </Link>
      </p>
    </div>
  );
}
